import Swal from 'sweetalert2';
import { saveActivity, deleteActivity, deleteClassAction } from '@/pages/Stablishments/actions';
import {
  buildRecurringSkipMarker,
  formatDayISO,
  getActivityUserIds,
  getFutureSeriesActivities,
  hasSkipMarkerForDate,
  isRecurringSeriesActivity,
  resolveRecurringTemplate,
} from '@/utils/activityUtils';
import { parseDateAbsolute } from '@/utils/dateUtils';

export default {
  methods: {
    normalizeDeletePayload(payload) {
      if (typeof payload === 'string') {
        const activity = this.activities.find((item) => item._id === payload);
        return { id: payload, activity, day: null };
      }
      return {
        id: payload.id || payload.activity?._id,
        activity: payload.activity,
        day: payload.day || null,
      };
    },
    async unsubscribeAllEnrolledUsers(activity, day = null) {
      const userIds = getActivityUserIds(activity);
      if (!userIds.length) {
        return;
      }

      const activityId = activity._id;
      const dayParam = day
        ? formatDayISO(day)
        : (activity.startDate ? formatDayISO(activity.startDate) : null);

      await Promise.all(
        userIds.map((userId) => deleteClassAction(activityId, userId, dayParam))
      );
    },
    async skipRecurringOnDate(template, day) {
      if (hasSkipMarkerForDate(this.activities, template._id, day)) {
        return;
      }
      await saveActivity(buildRecurringSkipMarker(template, day));
    },
    async deleteSingleDateOccurrence({ id, activity, day }) {
      if (activity && !activity.startDate && day) {
        await this.unsubscribeAllEnrolledUsers(activity, day);
        await this.skipRecurringOnDate(activity, day);
        return;
      }

      if (activity?.startDate && activity?.relatedActivity) {
        await this.unsubscribeAllEnrolledUsers(activity);
        await deleteActivity(id);
        const template = resolveRecurringTemplate(activity, this.activities);
        if (template) {
          const classDay = day || parseDateAbsolute(activity.startDate);
          await this.unsubscribeAllEnrolledUsers(template, classDay);
          await this.skipRecurringOnDate(template, classDay);
        }
        return;
      }

      if (activity?.startDate) {
        await this.unsubscribeAllEnrolledUsers(activity);
      }
      await deleteActivity(id);
    },
    async deleteAllFutureOccurrences(activity) {
      const template = resolveRecurringTemplate(activity, this.activities);
      if (!template) {
        await this.deleteSingleDateOccurrence({ id: activity._id, activity, day: null });
        return;
      }

      const futureActivities = getFutureSeriesActivities(this.activities, template);
      const datedFuture = futureActivities.filter((item) => item.startDate);

      for (const item of datedFuture) {
        const classDay = parseDateAbsolute(item.startDate);
        await this.unsubscribeAllEnrolledUsers(item, classDay);
        await deleteActivity(item._id);
      }

      await this.unsubscribeAllEnrolledUsers(template);
      await deleteActivity(template._id);
    },
    async confirmRecurringDelete() {
      const result = await Swal.fire({
        title: this.$t('deleteRecurringActivity'),
        text: this.$t('deleteRecurringActivityMessage'),
        icon: 'question',
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonText: this.$t('deleteOnlyThisDate'),
        denyButtonText: this.$t('deleteAllFutureOccurrences'),
        cancelButtonText: this.$t('cancel'),
        position: 'top',
      });

      if (result.isDismissed) {
        return null;
      }
      if (result.isConfirmed) {
        return 'single';
      }
      if (result.isDenied) {
        return 'future';
      }
      return null;
    },
    async deleteAct(payload) {
      if (this.isDemo && this.isDemo()) {
        return;
      }

      const { id, activity, day } = this.normalizeDeletePayload(payload);
      const isSeries = isRecurringSeriesActivity(activity, day);

      if (isSeries) {
        const choice = await this.confirmRecurringDelete();
        if (!choice) {
          return;
        }
        if (choice === 'single') {
          await this.deleteSingleDateOccurrence({ id, activity, day });
        } else {
          await this.deleteAllFutureOccurrences(activity);
        }
        await this.getAll();
        return;
      }

      const confirmResult = await Swal.fire({
        title: this.$t('areYouSure'),
        text: this.$t('youWontBeAbleToRevertThis'),
        type: 'warning',
        position: 'top',
        confirmButtonText: this.$t('delete'),
        showCancelButton: true,
      });

      if (!confirmResult.isConfirmed) {
        return;
      }

      await this.deleteSingleDateOccurrence({ id, activity, day });
      await this.getAll();
    },
  },
};
